import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Loader2, XCircle, Home, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FORMULES = {
  'domaine-complet': 'Domaine complet + Salle des fetes',
  'gite-complet': 'Gite complet (2 appartements)',
  'gite-salle': 'Gite + Salle des fetes',
  'gite-seul': 'Gite seul',
  'appartement-1': "L'Appartement Terrasse",
  'appartement-2': "L'Appartement Panorama",
};

function formatEuro(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(n || 0);
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('polling');
  const [txn, setTxn] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId || attempts >= 10) {
      if (attempts >= 10) setStatus('timeout');
      return;
    }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/payments/checkout/status/${sessionId}`);
      setTxn(res.data);

      if (res.data.payment_status === 'paid') {
        setStatus('paid');
        const clientEmail = res.data.metadata?.client_email || '';
        const resInfo = await axios.get(`${BACKEND_URL}/api/payments/reservation/${res.data.reservation_id}?email=${encodeURIComponent(clientEmail)}`);
        setReservation(resInfo.data.reservation);
      } else if (res.data.payment_status === 'expired') {
        setStatus('expired');
      } else {
        setStatus('polling');
        setAttempts(a => a + 1);
      }
    } catch {
      setStatus('error');
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (status === 'polling' && attempts < 10) {
      const timer = setTimeout(pollStatus, attempts === 0 ? 500 : 2000);
      return () => clearTimeout(timer);
    }
  }, [status, attempts, pollStatus]);

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6">
      <div className="w-full max-w-lg text-center">
        {status === 'polling' && (
          <div data-testid="payment-polling">
            <Loader2 className="w-16 h-16 text-[#8A9A5B] mx-auto mb-6 animate-spin" strokeWidth={1.5} />
            <h1 className="font-['Playfair_Display'] text-3xl text-[#2C3E30] mb-3">Verification du paiement...</h1>
            <p className="text-[#595959]">Merci de patienter quelques instants.</p>
          </div>
        )}

        {status === 'paid' && (
          <div data-testid="payment-success">
            <CheckCircle className="w-20 h-20 text-[#8A9A5B] mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#2C3E30] mb-3">Paiement confirme !</h1>
            <p className="text-[#595959] text-lg mb-8">
              Merci pour votre {txn?.payment_type === 'deposit' ? 'acompte' : 'paiement'}. Votre reservation est confirmee.
            </p>

            {reservation && (
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] text-left mb-8">
                <h2 className="font-['Playfair_Display'] text-xl text-[#2C3E30] mb-4">Recapitulatif</h2>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#595959]">Formule</span>
                    <span className="font-medium">{FORMULES[reservation.accommodation_type] || reservation.accommodation_type}</span>
                  </div>
                  {reservation.event_type && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#595959]">Evenement</span>
                      <span className="font-medium capitalize">{reservation.event_type.replace('_', ' ')}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-[#595959]">Dates</span>
                    <span className="font-medium">{reservation.arrival_date} → {reservation.departure_date}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#595959]">Voyageurs</span>
                    <span className="font-medium">{reservation.guests_lodging || reservation.guests}</span>
                  </div>
                  <hr className="border-[#E5E5E0]" />
                  <div className="flex justify-between text-sm">
                    <span className="text-[#595959]">Montant total</span>
                    <span className="font-semibold">{formatEuro(reservation.amount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#595959]">Paye aujourd'hui ({txn?.payment_type === 'deposit' ? 'acompte 30%' : 'solde'})</span>
                    <span className="font-semibold text-[#8A9A5B]">{formatEuro(txn?.amount)}</span>
                  </div>
                  {reservation.payment_status === 'deposit_paid' && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#595959]">Solde restant</span>
                        <span className="font-semibold text-[#C4A962]">{formatEuro(reservation.amount - reservation.deposit_amount)}</span>
                      </div>
                      {reservation.balance_due_date && (
                        <div className="flex justify-between text-sm">
                          <span className="text-[#595959]">Solde a regler avant le</span>
                          <span className="font-medium text-[#993333]">{reservation.balance_due_date}</span>
                        </div>
                      )}
                    </>
                  )}
                  {reservation.caution_amount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#595959]">Caution (autorisation bancaire)</span>
                      <span className="font-medium">{formatEuro(reservation.caution_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-[#595959]">Statut</span>
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border">
                      {reservation.payment_status === 'paid' ? 'Entierement paye' : 'Acompte recu'}
                    </Badge>
                  </div>
                  {reservation.deposit_refundable === false && (
                    <p className="text-xs text-[#993333] mt-2">Acompte non remboursable.</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                data-testid="back-home-btn"
                onClick={() => navigate('/')}
                className="rounded-full px-8 bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]"
              >
                <Home className="w-4 h-4 mr-2" strokeWidth={1.5} /> Retour au site
              </Button>
            </div>
          </div>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <div data-testid="payment-error">
            <XCircle className="w-20 h-20 text-[#993333] mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-['Playfair_Display'] text-3xl text-[#2C3E30] mb-3">
              {status === 'expired' ? 'Session expiree' : 'Verification echouee'}
            </h1>
            <p className="text-[#595959] mb-8">
              {status === 'expired'
                ? 'La session de paiement a expire. Veuillez reessayer.'
                : 'Nous n\'avons pas pu verifier votre paiement. Contactez-nous si le montant a ete debite.'}
            </p>
            <Button onClick={() => navigate('/')} className="rounded-full px-8 bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]">
              <Home className="w-4 h-4 mr-2" strokeWidth={1.5} /> Retour au site
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
